/**
 * Payment Service - Handles payment gateway operations for subscription activation and add-on purchase
 */
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { getAuthHeader } from './authService';

const API_BASE = API_CONFIG.BASE_URL;

/**
 * Create a payment order in Razorpay
 * @param {Object} paymentData - Payment order data
 * @param {number} paymentData.companyId - Company ID
 * @param {string} paymentData.billingCycle - 'monthly' or 'yearly'
 * @returns {Promise} Promise with payment order details
 */
export const createPaymentOrder = async (paymentData) => {
  try {
    const response = await axios.post(
      `${API_BASE}/payments/create-order`,
      paymentData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Create payment order error:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to create payment order';
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Verify payment after Razorpay payment completion
 * @param {Object} verificationData - Payment verification data
 * @param {string} verificationData.orderId - Razorpay order ID
 * @param {string} verificationData.paymentId - Razorpay payment ID
 * @param {string} verificationData.signature - Razorpay signature
 * @returns {Promise} Promise with verification result
 */
export const verifyPayment = async (verificationData) => {
  try {
    const response = await axios.post(
      `${API_BASE}/payments/verify`,
      verificationData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Verify payment error:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Payment verification failed';
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Create add-on payment order (tenant: company from auth).
 * @param {Object} data - { addonDefinitionId, quantityUnits, durationType }
 * @returns {Promise} Promise with Razorpay order details (orderId, amount, keyId, etc.)
 */
export const createAddonOrder = async (data) => {
  try {
    const response = await axios.post(
      `${API_BASE}/payments/create-addon-order`,
      {
        addonDefinitionId: data.addonDefinitionId,
        quantityUnits: data.quantityUnits ?? 1,
        durationType: data.durationType || 'REMAINING',
      },
      {
        headers: getAuthHeader(),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Create addon order error:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to create add-on payment order';
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Get payment status by order ID
 * @param {string} orderId - Razorpay order ID
 * @returns {Promise} Promise with payment status
 */
export const getPaymentStatus = async (orderId) => {
  try {
    const response = await axios.get(
      `${API_BASE}/payments/status/${orderId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get payment status error:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to get payment status';
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Get payment details by payment ID
 * @param {number} paymentId - Internal payment ID
 * @returns {Promise} Promise with payment details
 */
export const getPaymentById = async (paymentId) => {
  try {
    const response = await axios.get(
      `${API_BASE}/payments/${paymentId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get payment by ID error:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to get payment details';
      throw new Error(message);
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

export default {
  createPaymentOrder,
  createAddonOrder,
  verifyPayment,
  getPaymentStatus,
  getPaymentById
};

